import {
	ArrowDown,
	BookOpen,
	CheckCircle2,
	ChevronDown,
	Circle,
	Clock3,
	Search,
	SlidersHorizontal,
} from "lucide-react";
import * as React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useUserProfileContext } from "@/contexts/UserProfileContext";
import { CreditKeyWalkthrough, getCreditKeyGuideForRole } from "@/features/changelog/CreditKeyWalkthrough";
import { UpdateScreenshot } from "@/features/changelog/UpdateScreenshot";
import { AppPageLink } from "@/features/changelog/AppPageLink";
import {
	FEATURE_UPDATES,
	KIND_LABELS,
	STATUS_LABELS,
	filterUpdates,
	type FeatureUpdate,
	type UpdateStatus,
} from "@/features/changelog/entries";
import { cn } from "@/lib/utils";

const FILTERS = ["all", "live", "in-review", "planned"] as const;
type StatusFilter = (typeof FILTERS)[number];

function StatusBadge({ status }: { status: UpdateStatus }) {
	const Icon =
		status === "live" ? CheckCircle2 : status === "planned" ? Circle : Clock3;
	return (
		<span
			className={cn(
				"inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium",
				status === "live"
					? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"
					: status === "in-review"
						? "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-300"
						: "bg-muted text-muted-foreground",
			)}
		>
			<Icon className="size-3" />
			{STATUS_LABELS[status]}
		</span>
	);
}

function ReleaseDate({ entry, showVerificationDetails }: { entry: FeatureUpdate; showVerificationDetails: boolean }) {
	if (entry.status === "live" && entry.release?.verifiedAt) {
		return <>
			{entry.source && <span className="block font-medium text-foreground" title="Feature history month">{new Date(`${entry.source.date}T00:00:00Z`).toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "UTC" })}</span>}
			{showVerificationDetails && <span className="block">Verified live <time dateTime={entry.release.verifiedAt}>{new Date(`${entry.release.verifiedAt}T00:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" })}</time></span>}
		</>;
	}
	if (entry.status !== "live" || !entry.release)
		return (
			<>
				{entry.source && (
					<span className="block font-medium text-foreground">
						{new Date(`${entry.source.date}T00:00:00Z`).toLocaleDateString(
							"en-US",
							{ month: "long", year: "numeric", timeZone: "UTC" },
						)}
					</span>
				)}
				<span>
					{entry.status === "planned"
						? "No release date yet"
						: "Release unconfirmed"}
				</span>
			</>
		);
	return (
		<time dateTime={entry.release.date}>
			{new Date(`${entry.release.date}T00:00:00Z`).toLocaleDateString("en-US", {
				month: "short",
				day: "numeric",
				year: "numeric",
				timeZone: "UTC",
			})}
		</time>
	);
}

function UpdateArticle({
	entry,
	userRole,
	userEmail,
	showVerificationDetails,
}: {
	entry: FeatureUpdate;
	userRole?: string;
	userEmail?: string | null;
	showVerificationDetails: boolean;
}) {
	const [params] = useSearchParams();
	const detailsRef = React.useRef<HTMLDetailsElement>(null);
	React.useEffect(() => {
		if (params.get("update") === entry.id) {
			if (detailsRef.current) detailsRef.current.open = true;
			detailsRef.current?.scrollIntoView({
				block: "start",
				behavior: "instant",
			});
		}
	}, [entry.id, params]);
	const shareParams = new URLSearchParams();
	shareParams.set("update", entry.id);
	if (entry.kind === "fix") shareParams.set("fixes", "true");
	return (
		<article
			id={entry.id}
			className="grid gap-4 border-t border-border py-7 md:grid-cols-[130px_minmax(0,1fr)] md:gap-8"
		>
			<div className="flex items-center justify-between gap-3 md:block">
				<p className="text-xs leading-6 text-muted-foreground">
					<ReleaseDate entry={entry} showVerificationDetails={showVerificationDetails} />
				</p>
				<p className="text-xs font-medium md:mt-2">{entry.area}</p>
			</div>
			<div className="min-w-0">
				<div className="flex flex-wrap items-center gap-2.5">
					<span className="text-[11px] font-medium text-muted-foreground">
						{KIND_LABELS[entry.kind]}
					</span>
					<span className="text-border" aria-hidden="true">
						/
					</span>
					<StatusBadge status={entry.status} />
				</div>
				<h3 className="mt-4 text-[17px] font-medium leading-6 tracking-[-0.015em] text-balance">
					{entry.title}
				</h3>
				<p className="mt-3 max-w-2xl text-sm leading-7 text-muted-foreground text-pretty">
					{entry.summary}
				</p>
				<details ref={detailsRef} className="group mt-4 scroll-mt-6">
					<summary
						className={buttonVariants({
							variant: "outline",
							className:
								"min-h-11 w-fit cursor-pointer list-none gap-2 group-open:bg-muted [&::-webkit-details-marker]:hidden",
						})}
					>
						<BookOpen className="size-4" aria-hidden="true" />
						How it works
						<ChevronDown className="size-4 transition-transform group-open:rotate-180 motion-reduce:transition-none" aria-hidden="true" />
					</summary>
					<div className="mt-3 rounded-xl border border-border bg-muted/25 p-5 sm:p-6">
						<p className="text-[11px] font-medium tracking-wide text-muted-foreground uppercase">
							For {entry.audience}
						</p>
						<p className="mt-2 text-sm font-medium leading-6">
							{entry.benefit}
						</p>
						<ol className="mt-5 space-y-3">
							{entry.steps.map((step, index) => (
								<li key={step} className="flex gap-3 text-sm leading-6">
									<span className="flex size-6 shrink-0 items-center justify-center rounded-full border border-border bg-background text-[11px] font-medium tabular-nums">
										{index + 1}
									</span>
									<div className="min-w-0 space-y-2">
										<p>{step}</p>
										{entry.stepDestinations?.[index] && <AppPageLink destination={entry.stepDestinations[index]} userRole={userRole} userEmail={userEmail} isLive={entry.status === "live"} />}
									</div>
								</li>
							))}
						</ol>
						<UpdateScreenshot entryId={entry.id} userRole={userRole} />

						<p className="mt-5 border-t border-border pt-4 text-xs leading-6 text-muted-foreground">
							{entry.note}
						</p>
						<div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2">
							{!entry.stepDestinations && <AppPageLink destination={{ app: "internal", ...entry.destination }} userRole={userRole} userEmail={userEmail} isLive={entry.status === "live"} />}
							<Link
								to={`/changelog?${shareParams}`}
								className="inline-flex min-h-10 items-center rounded-md text-xs text-muted-foreground underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-ring"
								aria-label={`Link to update: ${entry.title}`}
							>
								Link to this update
							</Link>
						</div>
					</div>
				</details>
			</div>
		</article>
	);
}

export function ChangelogView({
	userRole,
	userEmail,
	entries = FEATURE_UPDATES,
	showVerificationDetails = false,
}: {
	userRole?: string;
	userEmail?: string | null;
	entries?: readonly FeatureUpdate[];
	showVerificationDetails?: boolean;
}) {
	const [params, setParams] = useSearchParams();
	const rawStatus = params.get("status");
	const status: StatusFilter =
		FILTERS.find((value) => value === rawStatus) ?? "all";
	const query = params.get("q") ?? "";
	const includeFixes = params.get("fixes") === "true";
	const updates = filterUpdates(entries, { status, query, includeFixes }).sort(
		(a, b) => {
			const aDate = a.source?.date ?? a.release?.date;
			const bDate = b.source?.date ?? b.release?.date;
			if (!aDate) return bDate ? 1 : 0;
			if (!bDate) return -1;
			return bDate.localeCompare(aDate);
		},
	);
	const liveCount = entries.filter(
		(entry) => entry.status === "live" && entry.kind !== "fix",
	).length;
	const spotlightUpdate = entries.find((entry) => entry.id === "credit-key-customer-guide");
	const allInReview =
		entries.length > 0 &&
		entries.every((entry) => entry.status === "in-review");
	function changeFilter(key: string, value: string) {
		setParams(
			(previous) => {
				const next = new URLSearchParams(previous);
				if (value) next.set(key, value);
				else next.delete(key);
				next.delete("update");
				return next;
			},
			{ replace: true, preventScrollReset: true },
		);
	}
	return (
		<div className="mx-auto max-w-[720px] pb-12 antialiased">
			<header className="flex flex-col justify-between gap-5 border-b border-border pt-3 pb-6 sm:flex-row sm:items-end sm:pt-6 sm:pb-7">
				<div>
					<p className="mb-3 text-[11px] font-normal text-muted-foreground">
						Cherry / Changelog
					</p>
					<h1 className="text-[16px] font-medium leading-6 tracking-[-0.01em]">
						What’s new in Cherry
					</h1>
					<p className="mt-4 max-w-md text-[13px] leading-6 text-muted-foreground">
						New tools, explained. Explore what’s changed and see how it works.
					</p>
				</div>
				<a
					href="#updates"
					className="inline-flex min-h-10 w-fit items-center gap-2 text-xs text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring"
				>
					Browse the archive
					<ArrowDown className="size-3.5" />
				</a>
			</header>

			{spotlightUpdate && getCreditKeyGuideForRole(userRole) && <section
				id="credit-key"
				className="pt-7 pb-10 sm:pt-8 sm:pb-12"
				aria-labelledby="spotlight-heading"
			>
				<div className="mb-3 flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
					<span className="text-[#b64e38] dark:text-[#e4a08c]">
						Feature story
					</span>
					<span aria-hidden="true">/</span>
					<span>Payments & customer storage</span>
					<span className="ml-auto tracking-normal normal-case">
						<StatusBadge status={spotlightUpdate.status} />
					</span>
				</div>
				<h2
					id="spotlight-heading"
					className="text-[21px] font-medium leading-7 tracking-[-0.025em] text-balance"
				>
					Meet Credit Key.
				</h2>
				<p className="mt-3 mb-5 max-w-xl text-[13px] leading-6 text-muted-foreground">
					A practical guide for your role, using the screens you’ll see in Cherry
					and on the customer website.
				</p>
				<CreditKeyWalkthrough userRole={userRole} userEmail={userEmail} isLive={spotlightUpdate.status === "live"} />
			</section>}

			<section
				id="updates"
				className="scroll-mt-6"
				aria-labelledby="updates-heading"
			>
				<div className="flex flex-wrap items-end justify-between gap-4 border-t border-border pt-8">
					<div>
						<p className="text-[10px] font-medium tracking-[0.15em] text-muted-foreground uppercase">
							The update log
						</p>
						<h2
							id="updates-heading"
							className="mt-2 text-[16px] font-medium tracking-tight"
						>
							The latest updates
						</h2>
					</div>
					<span className="text-xs text-muted-foreground tabular-nums">
						{liveCount} live {liveCount === 1 ? "update" : "updates"}
					</span>
				</div>
				<div className="mt-6 flex flex-col justify-between gap-4 lg:flex-row lg:items-center">
					<div
						className="flex flex-wrap gap-2"
						role="group"
						aria-label="Filter by release status"
					>
						{FILTERS.map((value) => (
							<Button
								type="button"
								key={value}
								variant={status === value ? "default" : "outline"}
								className="min-h-11"
								aria-pressed={status === value}
								onClick={() =>
									changeFilter("status", value === "all" ? "" : value)
								}
							>
								{value === "all" ? "All updates" : STATUS_LABELS[value]}
							</Button>
						))}
					</div>
					<div className="relative w-full lg:w-60">
						<Search className="pointer-events-none absolute top-3 left-3 size-4 text-muted-foreground" />
						<Input
							className="h-10 rounded-lg pl-9 text-xs"
							type="search"
							aria-label="Search updates"
							placeholder="Find an update…"
							value={query}
							onChange={(event) => changeFilter("q", event.target.value)}
						/>
					</div>
				</div>
				<div className="mt-4 mb-5 flex flex-wrap items-center justify-between gap-3">
					<p
						role="status"
						className="text-xs text-muted-foreground tabular-nums"
					>
						{updates.length} {updates.length === 1 ? "update" : "updates"}
						{query.trim() ? ` matching “${query.trim()}”` : " to explore"}
					</p>
					<label className="flex min-h-10 cursor-pointer items-center gap-2 text-xs text-muted-foreground">
						<SlidersHorizontal className="size-3.5" />
						<input
							type="checkbox"
							checked={includeFixes}
							onChange={(event) =>
								changeFilter("fixes", event.target.checked ? "true" : "")
							}
							className="size-3.5 accent-foreground"
						/>
						Include notable fixes
					</label>
				</div>
				{allInReview && (
					<p className="mb-6 flex gap-2 text-[11px] leading-6 text-muted-foreground">
						<Clock3 className="mt-1 size-3.5 shrink-0" />
						<span>
							Draft journal. Release availability is being checked before
							entries are marked live.
						</span>
					</p>
				)}
				{updates.length > 0 ? (
					updates.map((entry) => (
						<UpdateArticle
							key={entry.id}
							entry={entry}
							userRole={userRole}
							userEmail={userEmail}
							showVerificationDetails={showVerificationDetails}
						/>
					))
				) : (
					<div className="rounded-xl border border-dashed border-border px-6 py-14 text-center">
						<BookOpen className="mx-auto size-6 text-muted-foreground" />
						<h3 className="mt-4 text-base font-medium">
							{query || includeFixes
								? "No matching updates"
								: status === "live"
									? "No confirmed releases yet"
									: status === "planned"
										? "Nothing announced just yet"
										: "No updates here yet"}
						</h3>
						<p className="mx-auto mt-2 max-w-sm text-sm leading-6 text-muted-foreground">
							{status === "live" && !query
								? "Updates appear here once their production release is confirmed."
								: status === "planned" && !query
									? "Upcoming features will appear here when there’s a confirmed plan to share."
									: "Try a different search or return to all feature updates."}
						</p>
						<Button
							variant="outline"
							size="sm"
							className="mt-5 h-10"
							onClick={() =>
								setParams({}, { replace: true, preventScrollReset: true })
							}
						>
							Reset filters
						</Button>
					</div>
				)}
			</section>
			<footer className="mt-6 flex flex-wrap justify-between gap-3 border-t border-border pt-6 text-[11px] leading-6 text-muted-foreground">
				<p>Cherry product updates</p>
				<p>Features first. Notable fixes when they make a difference.</p>
			</footer>
		</div>
	);
}

export default function ChangelogPage() {
	const { user, profile, userRole } = useUserProfileContext();
	const userEmail =
		(user as { email?: string } | undefined)?.email ??
		(profile as { email?: string } | undefined)?.email;
	return <ChangelogView userRole={userRole} userEmail={userEmail} showVerificationDetails={process.env.NODE_ENV === "development"} />;
}
